import { ConversationType } from "../conversations/conversation";
import { CrawledUrl } from "../agent/agent";
import { RegistrationPlatformType } from "../registration-platform";

export interface BusinessMeta {
  appBannerDismissed?: boolean;
  appBannerDismissedSidebar?: boolean;
  appBannerDismissedConversations?: boolean;
  websiteTextingBannerDismissedSidebar?: boolean;
  websiteChatBannerDismissedSidebar?: boolean;
  crmIntegrationsBannerDismissedSidebar?: boolean;
  websiteChatSelected?: boolean;
  hubspotContactLimitToastDismissedAt?: string;
  disconnectedIntegrationToastDismissedAt?: string;
  onboardingIntent?: OnboardingIntent;
  [key: string]: any;
}

// Answers collected on the unified Customize step (ENG-2292). Stored raw under
// business.meta.onboarding_intent; nothing gets enabled from them — they only
// drive the recommended plan and the Go Live checklist.
export type OnboardingIntentAnswer = 'yes' | 'no' | 'not_sure';
export type OnboardingBookingMode = 'direct' | 'link';

export interface OnboardingIntent {
  bookAppointments?: OnboardingIntentAnswer | null;
  bookingMode?: OnboardingBookingMode | null;
  transferCalls?: OnboardingIntentAnswer | null;
  textALink?: OnboardingIntentAnswer | null;
}

// Which guided-setup flow the API resolved for the business (ENG-2292). The
// admin branches ONLY on 'unified'; every other value keeps today's flows.
export type OnboardingFlow = 'unified' | 'legacy';

export interface CrmContactLimit {
  provider: string;
  reachedAt: string;
}

export interface DisconnectedIntegration {
  referenceId: string;
  name: string;
  disconnectedAt: string;
}

export interface Business {
  title?: string;
  url?: string;
  id?: string;
  meta?: BusinessMeta;
  timezone?: string;
  information?: BusinessInformation;
  services?: Service[];
  transferDepartments?: TransferDepartment[];
  textALinkMessages?: TextLink[];
  phoneNumbers?: BusinessPhoneNumber[];
  billingVersion?: number;
  billingProvider?: string;
  onboardingFlow?: OnboardingFlow;
  // Lowercase plan key ('scale' | 'professional') computed by the API from the
  // onboarding intent.
  recommendedPlan?: string;
  // A canceled Stripe business with an unused trial reset (ENG-2063). The
  // dashboard blocks on a card until it subscribes again (ENG-2367).
  trialResetPending?: boolean;
  registrationSource?: RegistrationPlatformType;
  hasCommerceApp?: boolean;
  commerceShopDomain?: string;
  faqLimit?: number;
  scenarioLimit?: number | null;
  transferCallsLimit?: number;
  textALinkLimit?: number;
  optInVerificationEnabled?: boolean;
  waterfallBackupNumbersLimit?: number;
  crmContactLimit?: CrmContactLimit | null;
  disconnectedIntegrations?: DisconnectedIntegration[];
}

export interface BusinessInformation {
  description: string;
  urls: string[];
  notificationCallTypes: ConversationType[];
  googleMapsUrl: string;
  phone: string;
  additionalProperties?: AdditionalProperties;
  meta: any;
  id: string;
}

export interface BusinessData {
  title: string;
  websiteUrls: string[];
  industry: string;
}

export interface Service {
  id?: string;
  name?: string;
  isActive?: boolean;
}

export interface Area {
  isActive: boolean;
  name: string;
}
export interface Urls {
  homePage: string;
  googleMaps: string;
}

export interface AdditionalProperties {
  name?: string;
  email?: string;
  addresses?: Address[];
  summary?: string;
  industry?: string;
  socialMedia?: string[];
  phoneNumbers?: string[];
  serviceAreas?: Area[];
  businessHours?: BusinessHours[];
  specialHours?: SpecialHours[];
  timezone?: string;
  placeId?: string;
  url?: string;
  // Resolved by the API during retrain: rehosted Google place photo, or the
  // site favicon when training from a website (ENG-2201).
  imageUrl?: string;
  notifications?: NotificationChannel[];
  crawledUrls?: CrawledUrl[];
  additionalContext?: string;
  titlePronunciation?: string;
}

export interface Address {
  city: string;
  state: string;
  street: string;
  zipCode: string;
  id?: string;
}

export interface TextLink {
  id?: string;
  message: string;
  url: string;
  department: string;
}

export interface BusinessHours {
  day: string;
  open: string;
  close: string;
}

// One-off closures or custom hours for specific dates (ENG-2393). Dates are
// 'YYYY-MM-DD' in the business timezone and times are 24h 'HH:MM'.
export interface SpecialHours {
  id: string;
  dates: string[];
  closed: boolean;
  hours: SpecialHoursRange[];
  reason: string | null;
}

export interface SpecialHoursRange {
  open: string;
  close: string;
}

export interface BusinessPhoneNumber {
  id: string;
  number: string;
  isPrimary: boolean;
  type: BusinessPhoneNumberType;
  isActive: boolean;
  countryPrefix?: string;
}

export enum BusinessPhoneNumberType {
  Main = 'main',
  Test = 'test',
}

export interface TransferDepartment {
  id?: string | null;
  type?: TransferDepartmentType;
  department?: string;
  destinations?: {
    number: string;
    label: string;
    countryPrefix?: string;
  }[];
  isWarmTransfer?: boolean;
  availabilityType?: TransferDepartmentAvailabilityType;
  customHours?: BusinessHours[];
  holdMusic?: string;
  configuration?: {
    holdMusic?: string;
  };
  collectInfoBeforeTransfer?: boolean;
  collectInfoPrompt?: string;
  triggerDescription?: string;
}

export enum TransferDepartmentType {
  Simple = 'cold',
  Live = 'warm',
  Waterfall = 'waterfall'
}

export enum TransferDepartmentAvailabilityType {
  Always = 'always',
  BusinessHours = 'business_hours',
  Custom = 'custom',
  None = 'none'
}

export interface NotificationChannel {
  type: NotificationChannelType;
  destination?: string;
  configuration?: {
    callTypes?: any;
  }
}

export enum NotificationChannelType {
  Sms = 'sms',
  Email = 'email',
  Push = 'push'
}